import React, { useEffect, useState } from "react";
import { Box, useTheme } from "@mui/material";
import { useGetQuotesQuery } from "state/api";
import Header from "components/Header";
import { DataGrid } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import DataGridCustomToolbar from "components/DataGridCustomToolbar";
import Spinner from "components/Spinner";

const Quotes = () => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [sort, setSort] = useState({});
  const [search, setSearch] = useState("");
  const [filterModel, setFilterModel] = useState({ items: [] });
  const { data, isLoading, refetch } = useGetQuotesQuery({
    page,
    pageSize,
    sort: JSON.stringify(sort),
    search,
    filter: JSON.stringify(filterModel.items),
  });
  const [searchInput, setSearchInput] = useState("");
  const navigate = useNavigate();
  const theme = useTheme();
  const [columnsVisibility, setColumnsVisibility] = useState({
    "job-quote.name": true,
    "job.name": true,
    quotedValue: true,
    "job.type": true,
    status: true,
    "job.urgency": true,
    quoteDate: true,
    "job.clientId": true,
  });

  // Fetch jobs when component mounts and whenever page, pageSize, sort, or search changes
  useEffect(() => {
    refetch();
  }, [page, pageSize, sort, search, refetch]);

  // Refetch jobs when the page becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refetch();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Cleanup the event listener when the component unmounts
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [refetch]);

  const columns = [
    {
      field: "job-quote.name",
      headerName: "Quote ID",
      flex: 0.5,
      valueGetter: (params) =>
        params.row.job?.name
          ? `${params.row.job.name}-${params.row.quoteNumber}`
          : "N/A",
    },
    {
      field: "job.name",
      headerName: "Job Name",
      flex: 0.2,
      valueGetter: (params) => params.row.job?.name ?? "N/A",
    },
    {
      field: "job.clientId",
      headerName: "Client",
      flex: 0.3,
      valueGetter: (params) => params.row.job?.clientId?.businessName ?? "N/A",
    },
    {
      field: "quotedValue",
      headerName: "Value",
      flex: 0.2,
      renderCell: (params) => {
        let cost = params.row.quotedValue ?? "N/A";
        if (params.row.acceptedSpeed === "advanced") {
          cost = params.row.quotedValue ?? "N/A";
        } else if (params.row.acceptedSpeed === "faster") {
          cost = params.row.quotedFasterValue ?? "N/A";
        } else if (params.row.acceptedSpeed === "urgent") {
          cost = params.row.quotedUrgentValue ?? "N/A";
        } else {
          cost = params.row.quotedValue ?? "N/A";
        }
        const currency = params.row.quotedCurrency ?? "";
        return `${cost} ${currency}`;
      },
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.2,
      valueGetter: (params) => params.row.status ?? "N/A",
    },
    {
      field: "quoteDate",
      headerName: "Quote Date",
      flex: 0.3,
      valueGetter: (params) => {
        const date = params.row.quoteDate;
        if (!date || !Number.isFinite(new Date(date).getTime())) {
          return "N/A";
        }
        return new Intl.DateTimeFormat("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "2-digit",
          hour: "numeric",
          minute: "numeric",
        }).format(new Date(date));
      },
    },
    {
      field: "job.urgency",
      headerName: "Urgency",
      flex: 0.2,
      valueGetter: (params) => params.row.job?.urgency ?? "N/A",
    },
  ];

  const toggleColumnVisibility = (field) => {
    setColumnsVisibility((prevState) => ({
      ...prevState,
      [field]: !prevState[field],
    }));
  };

  const getColumns = () => {
    return columns.map((column) => ({
      ...column,
      hide: !columnsVisibility[column.field],
    }));
  };

  // Function to update filterModel, passed down to DataGridCustomToolbar
  const handleFilterModelChange = (newModel) => {
    setFilterModel(newModel);
  };

  if (!data || isLoading) return <Spinner />;

  return (
    <Box m="1rem 2.5rem 0rem 2.5rem">
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        px="2rem"
      >
        <Header title="Quotes" subtitle="List of quotes" />
      </Box>
      <Box
        mt="40px"
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: theme.palette.background.alt,
            color: theme.palette.secondary[100],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: theme.palette.primary.light,
          },
          "& .MuiDataGrid-footerContainer": {
            backgroundColor: theme.palette.background.alt,
            color: theme.palette.secondary[100],
            borderTop: "none",
          },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${theme.palette.secondary[200]} !important`,
          },
        }}
      >
        <DataGrid
          loading={isLoading || !data}
          getRowId={(row) => row._id}
          rows={(data && data.quotes) || []}
          sx={{
            padding: "1rem",
            backgroundColor: theme.palette.background.alt,
            ".MuiDataGrid-virtualScroller": {
              backgroundColor: theme.palette.background.alt,
              borderTop: "2px solid rgba(224, 224, 224, 1)",
              overflowX: "hidden",
            },
          }}
          columns={getColumns()}
          rowCount={(data && data.total) || 0}
          rowsPerPageOptions={[20, 50, 100]}
          pagination
          page={page}
          pageSize={pageSize}
          paginationMode="server"
          onPageChange={(newPage) => setPage(newPage)}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          onSortModelChange={(newSortModel) => setSort(...newSortModel)}
          components={{ Toolbar: DataGridCustomToolbar }}
          componentsProps={{
            toolbar: {
              searchInput,
              setSearchInput,
              setSearch,
              columns,
              columnsVisibility,
              toggleColumnVisibility,
              onFilterModelChange: handleFilterModelChange,
            },
          }}
          onRowClick={(e) => {
            navigate("/quotes/" + e.id);
          }}
        />
      </Box>
    </Box>
  );
};

export default Quotes;
